import React, { useRef, useLayoutEffect, useState, memo, useMemo } from 'react';

function formatTime(val) {
  if (!val) return "";
  const d = new Date(val);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }).toUpperCase();
}

function formatDow(val) {
  if (!val) return "";
  const d = new Date(val);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString([], { weekday: "short" }).toUpperCase();
}

function formatMonthDay(val) {
  if (!val) return "";
  const d = new Date(val);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString([], { month: "short", day: "numeric" }).toUpperCase();
}

/** Get due date as a Date object from an assignment */
function getAssignmentDueDate(a) {
  const raw = a?.dueAt ?? a?.due_at ?? a?.dueDate ?? a?.due ?? a?.dueDatetime ?? null;
  if (!raw) return null;
  const d = new Date(raw);
  return isNaN(d.getTime()) ? null : d;
}

/** Get a day key (YYYY-MM-DD) for grouping */
function dayKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function isDone(a) {
  return !!(a?.isDone ?? a?.completed ?? a?.isCompleted);
}

/** Returns true if the event starts within the next 48 hours */
function isWithin48Hours(dateVal) {
  if (!dateVal) return false;
  const d = new Date(dateVal);
  if (isNaN(d.getTime())) return false;
  const now = Date.now();
  const diff = d.getTime() - now;
  return diff >= 0 && diff <= 48 * 60 * 60 * 1000;
}

const UpcomingCourseCard = memo(function UpcomingCourseCard({
  course,
  // legacy props
  nextEvent,
  dueAssignments = [],
  // new unified props
  mode = 'upcoming', // 'upcoming' | 'date'
  event = null, // single event for upcoming mode
  events = [], // array of events for date mode
  assignments = [],
  headerLabel = null,
  onToggleAssignment
}) {
  const courseTitle = course?.title || course?.name || 'Course';

  // Helper to extract start ms (robust across various API shapes)
  const getStartMs = (ev) => {
    if (!ev) return null;
    const v = ev?.start ?? ev?.startTime ?? ev?.start_at ?? ev?.startAt ?? ev?.startDatetime ?? ev?.startDate ?? ev?.eventDate ?? ev?.date ?? ev?.__startMs ?? null;
    const d = new Date(v);
    return isNaN(d.getTime()) ? null : d.getTime();
  };

  // Determine event row depending on mode
  let eventRow = null;
  let extraEvents = 0;
  if (mode === 'date') {
    const evs = Array.isArray(events) ? events.filter(Boolean) : [];
    evs.sort((a,b) => (getStartMs(a) || 0) - (getStartMs(b) || 0));
    if (evs.length) {
      eventRow = evs[0];
      extraEvents = Math.max(0, evs.length - 1);
    }
  } else {
    eventRow = event || nextEvent || null;
  }

  const eventStart = eventRow ? (eventRow.start ?? eventRow.startTime ?? eventRow.start_at ?? eventRow.startAt ?? eventRow.startDatetime ?? eventRow.startDate ?? eventRow.eventDate ?? eventRow.date ?? eventRow.__startMs ?? null) : null;

  const urgent = useMemo(() => isWithin48Hours(eventStart), [eventStart]);

  // Determine assignment groups — group by actual due date so users see specific deadlines
  const useAssignments = useMemo(() => (assignments && assignments.length) ? assignments : (dueAssignments || []), [assignments, dueAssignments]);
  const dueGroups = useMemo(() => {
    if (!useAssignments || !useAssignments.length) return [];

    // Get the event's date key for comparison
    const eventDate = eventStart ? new Date(eventStart) : null;
    const eventDayKey = eventDate ? dayKey(eventDate) : null;

    const groupMap = new Map();
    useAssignments.forEach(a => {
      const dueDate = getAssignmentDueDate(a);
      if (!dueDate) {
        // Fallback for assignments with no parseable due date
        const k = 'unknown';
        if (!groupMap.has(k)) groupMap.set(k, { key: k, label: 'Due this class:', sortMs: Infinity, items: [] });
        groupMap.get(k).items.push(a);
        return;
      }
      const k = dayKey(dueDate);
      if (!groupMap.has(k)) {
        // If the assignment is due on the same day as the class, say "Due at class:"
        let label;
        if (eventDayKey && k === eventDayKey) {
          label = 'Due at class:';
        } else {
          const dateLabel = dueDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }).toUpperCase();
          label = `Due ${dateLabel}:`;
        }
        groupMap.set(k, { key: k, label, sortMs: dueDate.getTime(), items: [] });
      }
      groupMap.get(k).items.push(a);
    });

    // Sort groups chronologically
    return Array.from(groupMap.values()).sort((a, b) => a.sortMs - b.sortMs);
  }, [useAssignments, eventStart]);

  return (
    <div className={"upcomingCourseCard" + (urgent ? " upcomingCourseCard--urgent" : "")}>
      {/* Card header: date tile + text column */}
      <div
        className="upcomingCardHeaderRow"
        role="button"
        tabIndex={0}
        onClick={() => {
          if (eventRow) {
            try {
              window.dispatchEvent(new CustomEvent('scoutiq:openEventModal', {
                detail: { event: eventRow }
              }));
            } catch (e) {
              console.warn(e);
            }
          }
        }}
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.key === ' ') && eventRow) {
            e.preventDefault();
            try {
              window.dispatchEvent(new CustomEvent('scoutiq:openEventModal', {
                detail: { event: eventRow }
              }));
            } catch (err) {}
          }
        }}
      >
        {/* Date tile */}
        {eventRow && (
          <div className="upcomingDateTile">
            <span className="upcomingDateTile__dow">{formatDow(eventStart)}</span>
            <span className="upcomingDateTile__date">{formatMonthDay(eventStart)}</span>
            <span className="upcomingDateTile__time">{formatTime(eventStart)}</span>
          </div>
        )}

        {/* Text column: course + event title */}
        <div className="upcomingCardHeaderText">
          <div className="upcomingCourseTitle">{courseTitle}</div>
          {eventRow ? (
            <div className="upcomingNextTitle">
              {eventRow.title || eventRow.name || "Class"}
              {extraEvents > 0 && (
                <span className="upcomingNextExtra"> +{extraEvents} more</span>
              )}
            </div>
          ) : (
            <div className="upcomingNextTitle upcomingNextTitleEmpty">No upcoming class</div>
          )}
        </div>
      </div>

      {/* Divider between header and assignments */}
      <div className="upcomingEventAssignmentDivider" />

      {/* Assignments */}
      <div className="upcomingAssignmentsWrap">
        <div className="upcomingAssignmentsScroll">
          {dueGroups.length ? (
            dueGroups.map(group => (
              <div key={group.key} className="upcomingDueGroup">
                <div className="upcomingDueHeader">{group.label}</div>

                <div className="upcomingAssignmentList">
                  {group.items.map(a => {
                    const full = a.title || a.name || "Assignment";
                    const stableKey = a.id || a.assignmentId || a._id || a.assignmentID || `temp-${full.slice(0, 20)}`;
                    return (
                      <AssignmentButton
                        key={stableKey}
                        assignment={a}
                        fullTitle={full}
                        isDone={isDone(a)}
                        onToggle={() => onToggleAssignment?.(a)}
                      />
                    );
                  })}
                </div>
              </div>
            ))
          ) : (
            <div className="upcomingNoAssignments">No assignments due</div>
          )}
        </div>
      </div>
    </div>
  );
});

export default UpcomingCourseCard;

const AssignmentButton = memo(function AssignmentButton({ assignment, fullTitle, isDone, onToggle }) {
  // Track a key that increments each time we transition to checked,
  // forcing React to remount the span so CSS animations replay fresh.
  const [animKey, setAnimKey] = useState(0);
  const prevDone = useRef(isDone);

  useLayoutEffect(() => {
    if (isDone && !prevDone.current) {
      setAnimKey(k => k + 1);
    }
    prevDone.current = isDone;
  }, [isDone]);

  return (
    <button
      className={"upcomingAssignmentRow " + (isDone ? "isDone" : "")}
      type="button"
      onClick={(e) => {
        // If click target is the check element, treat as toggle; otherwise open assignment modal
        if (e.target.closest('.upcomingCheck')) {
          try { onToggle?.(); } catch (err) { console.warn(err); }
          return;
        }
        try {
          window.dispatchEvent(new CustomEvent('scoutiq:openAssignmentModal', { detail: { assignment } }));
        } catch (err) { console.warn(err); }
      }}
      aria-label={fullTitle}
      data-full={fullTitle}
    >
      <span key={animKey} className={"upcomingCheck " + (isDone ? "checked" : "")} aria-hidden="true" />
      <span className="upcomingAssignmentTitle">
        {fullTitle}
        {isDone && <span key={`s${animKey}`} className="upcomingStrikeLine" />}
      </span>
    </button>
  );
});
